import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import {
    getSalesReactBaseUrl,
    getLiveDataClients,
    getLiveDataBranches,
    resetLiveDataMasters,
    probeGsbillingHost,
    gsbillingLoginAuthenticate,
    syncGsbillingCounterData,
    getGsbillingBillDetails,
    sendGsbillingSms,
    sendGsbillingSalesTrackingRecords,
    getGsbillingClientCountUpdation,
    getGsbillingApplicationSetting,
    callbackGsbillingApplicationSetting,
    getGsbillingMastersCreation,
    callbackGsbillingMastersCreation,
    getGsbillingCreditCustomer,
    callbackGsbillingCreditCustomer,
    getGsbillingOutletDetails,
    callbackGsbillingOutletDetails,
    getGsbillingProductCategory,
    callbackGsbillingProductCategory,
    getGsbillingProduct,
    callbackGsbillingProduct,
    getGsbillingUsers,
    callbackGsbillingUsers,
    getGsbillingPrinterDetails,
    callbackGsbillingPrinterDetails,
    getGsbillingNotifications,
    callbackGsbillingNotifications
} from '../utilities/api';

/** Handlers for endpoints that take only the branch/segment in the URL and no request body. */
const ENDPOINT_HANDLERS = {
    get_billdetails: getGsbillingBillDetails,
    send_sms: sendGsbillingSms,
    sales_tracking_records: sendGsbillingSalesTrackingRecords,
    get_client_countupdation: getGsbillingClientCountUpdation,
    Get_application_setting: getGsbillingApplicationSetting,
    Get_masters_creation: getGsbillingMastersCreation,
    Get_credit_customer: getGsbillingCreditCustomer,
    Get_outlet_deatils: getGsbillingOutletDetails,
    Get_product_category: getGsbillingProductCategory,
    Get_product: getGsbillingProduct,
    Get_users: getGsbillingUsers,
    Get_printer_details: getGsbillingPrinterDetails,
    Get_notifications: getGsbillingNotifications
};

/** POST callback handlers used to acknowledge (mark as synced) pending master data rows. */
const CALLBACK_HANDLERS = {
    callback_application_setting: callbackGsbillingApplicationSetting,
    callback_masters_creation: callbackGsbillingMastersCreation,
    callback_credit_customer: callbackGsbillingCreditCustomer,
    callback_outlet_deatils: callbackGsbillingOutletDetails,
    callback_product_category: callbackGsbillingProductCategory,
    callback_product: callbackGsbillingProduct,
    callback_users: callbackGsbillingUsers,
    callback_printer_details: callbackGsbillingPrinterDetails,
    callback_notifications: callbackGsbillingNotifications
};

// `host` mirrors the backend's split: 'server' endpoints are served by client_creation.server_url
// when that deployment answers, falling back to base_url; 'client' endpoints read/write the client's
// own billing tables and are always served by base_url. 'local' stays on the backend hosting this UI.
const ENDPOINTS = [
    { key: 'login_authenticate', label: 'Login Authenticate', method: 'POST', path: 'api/gsbilling/login_authenticate', group: 'Auth & Bill Sync', noBranch: true, host: 'server' },
    { key: 'counterdata_synching', label: 'Counter Data Synching', method: 'POST', path: 'api/gsbilling/counterdata_synching/{segment}', group: 'Auth & Bill Sync', host: 'client' },
    { key: 'get_billdetails', label: 'Get Bill Details', method: 'POST', path: 'api/gsbilling/get_billdetails/{segment}', group: 'Auth & Bill Sync', host: 'client' },
    { key: 'send_sms', label: 'Send SMS', method: 'POST', path: 'api/gsbilling/send_sms/{segment}', group: 'Auth & Bill Sync', host: 'client' },
    { key: 'sales_tracking_records', label: 'Sales Tracking Records', method: 'POST', path: 'api/gsbilling/sales_tracking_records/{segment}', group: 'Auth & Bill Sync', host: 'client', hidden: true },

    { key: 'get_client_countupdation', label: 'Client Count Updation', method: 'GET', path: 'api/gsbilling/get_client_countupdation/{segment}', group: 'Master Data Sync', host: 'server' },

    { key: 'Get_application_setting', label: 'Application Setting', method: 'GET', path: 'api/gsbilling/Get_application_setting/{segment}', group: 'Master Data Sync', host: 'server' },
    { key: 'callback_application_setting', label: 'Callback Application Setting', method: 'POST', path: 'api/gsbilling/callback_application_setting/{segment}', group: 'Master Data Sync', host: 'server', isCallback: true, sourceKey: 'Get_application_setting', idField: 'app_setting_id' },

    { key: 'Get_masters_creation', label: 'Masters Creation', method: 'GET', path: 'api/gsbilling/Get_masters_creation/{segment}', group: 'Master Data Sync', host: 'server' },
    { key: 'callback_masters_creation', label: 'Callback Masters Creation', method: 'POST', path: 'api/gsbilling/callback_masters_creation/{segment}', group: 'Master Data Sync', host: 'server', isCallback: true, sourceKey: 'Get_masters_creation', idField: 'masterId' },

    { key: 'Get_credit_customer', label: 'Company/Customers', method: 'GET', path: 'api/gsbilling/Get_credit_customer/{segment}', group: 'Master Data Sync', host: 'server' },
    { key: 'callback_credit_customer', label: 'Callback Company/Customers', method: 'POST', path: 'api/gsbilling/callback_credit_customer/{segment}', group: 'Master Data Sync', host: 'server', isCallback: true, sourceKey: 'Get_credit_customer', idField: 'cust_id' },

    { key: 'Get_outlet_deatils', label: 'Outlet Details', method: 'GET', path: 'api/gsbilling/Get_outlet_deatils/{segment}', group: 'Master Data Sync', host: 'server' },
    { key: 'callback_outlet_deatils', label: 'Callback Outlet Details', method: 'POST', path: 'api/gsbilling/callback_outlet_deatils/{segment}', group: 'Master Data Sync', host: 'server', isCallback: true, sourceKey: 'Get_outlet_deatils', idField: 'outId' },

    { key: 'Get_product_category', label: 'Product Category', method: 'GET', path: 'api/gsbilling/Get_product_category/{segment}', group: 'Master Data Sync', host: 'server' },
    { key: 'callback_product_category', label: 'Callback Product Category', method: 'POST', path: 'api/gsbilling/callback_product_category/{segment}', group: 'Master Data Sync', host: 'server', isCallback: true, sourceKey: 'Get_product_category', idField: 'pr_cat_id' },

    { key: 'Get_product', label: 'Product', method: 'GET', path: 'api/gsbilling/Get_product/{segment}', group: 'Master Data Sync', host: 'server' },
    { key: 'callback_product', label: 'Callback Product', method: 'POST', path: 'api/gsbilling/callback_product/{segment}', group: 'Master Data Sync', host: 'server', isCallback: true, sourceKey: 'Get_product', idField: 'pr_id' },

    { key: 'Get_users', label: 'Users', method: 'GET', path: 'api/gsbilling/Get_users/{segment}', group: 'Master Data Sync', host: 'server' },
    { key: 'callback_users', label: 'Callback Users', method: 'POST', path: 'api/gsbilling/callback_users/{segment}', group: 'Master Data Sync', host: 'server', isCallback: true, sourceKey: 'Get_users', idField: 'userId' },

    { key: 'Get_printer_details', label: 'Printer Details', method: 'GET', path: 'api/gsbilling/Get_printer_details/{segment}', group: 'Master Data Sync', host: 'server' },
    { key: 'callback_printer_details', label: 'Callback Printer Details', method: 'POST', path: 'api/gsbilling/callback_printer_details/{segment}', group: 'Master Data Sync', host: 'server', isCallback: true, sourceKey: 'Get_printer_details', idField: 'printer_id', hidden: true },

    { key: 'reset_masters', label: 'Reset All Masters', method: 'POST', path: 'api/server_masters/master_table_reset/{segment}', group: 'Masters Data Reset', host: 'local' },

    { key: 'Get_notifications', label: 'Notifications', method: 'GET', path: 'api/gsbilling/Get_notifications/{segment}', group: 'Notifications', host: 'server' },
    { key: 'callback_notifications', label: 'Callback Notifications', method: 'POST', path: 'api/gsbilling/callback_notifications/{segment}', group: 'Notifications', host: 'server', isCallback: true, sourceKey: 'Get_notifications', idField: 'notification_id' }
];

// `hidden` entries stay wired up (route, handler, api helper) but are kept out of the
// list and the coverage stats — otherwise the counters could never reach 100%. Flip the
// flag off to bring one back.
const VISIBLE_ENDPOINTS = ENDPOINTS.filter((e) => !e.hidden);

const IS_LOCAL = process.env.NODE_ENV === 'development';

/** Which deployment a request is sent to. The backend serving this page is only worth
 *  offering while developing against it, so it stays out of the list in production. */
const HOST_MODES = [{ label: 'Server', value: 'server' }, { label: 'Client', value: 'client' }, ...(IS_LOCAL ? [{ label: 'Local', value: 'local' }] : [])];

const HOST_LABELS = { server: 'SERVER', client: 'CLIENT', local: 'LOCAL' };

const GROUP_STYLES = {
    'Auth & Bill Sync': { color: '#1d4ed8', bg: '#eff6ff', accent: '#3b82f6', soft: 'rgba(59, 130, 246, 0.10)', icon: 'pi pi-lock' },
    'Master Data Sync': { color: '#0f766e', bg: '#f0fdfa', accent: '#14b8a6', soft: 'rgba(20, 184, 166, 0.10)', icon: 'pi pi-database' },
    'Masters Data Reset': { color: '#be123c', bg: '#fff1f2', accent: '#f43f5e', soft: 'rgba(244, 63, 94, 0.10)', icon: 'pi pi-refresh' },
    Notifications: { color: '#6d28d9', bg: '#f5f3ff', accent: '#8b5cf6', soft: 'rgba(139, 92, 246, 0.10)', icon: 'pi pi-bell' }
};

const DEFAULT_GROUP_STYLE = { color: '#374151', bg: '#f9fafb', accent: '#9ca3af', soft: 'rgba(156, 163, 175, 0.12)', icon: 'pi pi-bookmark' };
const groupStyle = (group) => GROUP_STYLES[group] || DEFAULT_GROUP_STYLE;

const LOGIN_TEMPLATE = JSON.stringify({ username: '', password: '' }, null, 2);
const COUNTERDATA_TEMPLATE = JSON.stringify(
    {
        Order: [
            {
                bill_id: '',
                in_no: '',
                invoice_no: '',
                invoice_date: '', // Y-m-d
                invoice_time: '', // h:i A  e.g. 07:45 PM
                cust_name: '',
                cust_phone: '',
                total_amount: '',
                paid_amt: '',
                mop: 'CASH',
                final_mop: 'CASH',
                branch: '',
                user_id: '',
                order_type: 'COUNTER',
                status: 'synched',
                counter_items: [],
                counter_payments: []
            }
        ]
    },
    null,
    2
);
const CALLBACK_TEMPLATE = JSON.stringify({ data: [] }, null, 2);


/** Hints for endpoints that send no request body, keyed by endpoint key. */
const NO_BODY_HINTS = {
    get_billdetails: 'Select a branch and click Run to read back the highest bill / item / payment ids for that branch. No request body is used. An unknown or inactive branch answers HTTP 404.',
    send_sms: "Select a branch and click Run to trigger today's closing-day-sales SMS for that branch. No request body is used. The backend only relays this to sales_tracking_records on its own host.",
    sales_tracking_records:
        "The endpoint send_sms relays to — builds today's closing-day-sales message from this branch's own bill and payment tables, sends it, and writes an sms_sent_details row. No request body is used. It reads the per-branch billing tables, so it only answers on the client deployment.",
    Get_printer_details: 'Select a branch and click Run to list its printers. printer_details has no data_transfer column, so this is not an incremental pull — every printer on the branch comes back each time.',
    Get_notifications:
        "Select a branch and click Run to list the alerts still pending for it — the ones addressed to it plus the client-wide ones. notifications has no data_transfer column, so 'Closed' plays that role: everything not yet closed comes back, and Callback Notifications closes it. Expired alerts are held back whatever their status. An unknown branch answers with an empty list and an error field."
};

const displayBaseUrl = () => (process.env.NODE_ENV === 'development' ? 'http://localhost/Counter-Backend' : getSalesReactBaseUrl());

const trimUrl = (url) => String(url || '').replace(/\/+$/, '');

const rawResultText = (data) => (typeof data === 'object' ? JSON.stringify(data, null, 2) : String(data));

/**
 * A cross-host call that dies without a response is almost always CORS or a dead host —
 * axios reports both as a bare "Network Error", so spell out what to check.
 */
const describeError = (err, endpoint) => {
    if (err.response) return err.message;
    const host = endpoint?.host === 'client' ? 'client' : 'server';
    return `${err.message}\n\nNo HTTP response came back. Either that ${host} deployment is down, or the browser blocked the call: this page's origin must be listed in the target's Config/Cors.php allowedOrigins. Switch Host to "This backend (local)" to test against the backend serving this UI.`;
};

// Get_application_setting and Get_printer_details return their rows under `Details`; every other pull endpoint uses `data`.
const extractIds = (raw, idField) => {
    const rows = Array.isArray(raw) ? raw : Array.isArray(raw?.data) ? raw.data : Array.isArray(raw?.Details) ? raw.Details : [];
    return rows.map((r) => r?.[idField]).filter((v) => v !== undefined && v !== null);
};

/** Compact status / method chip used across the list and detail pane. */
const Pill = ({ tone, children, className = '' }) => <span className={`gb-pill gb-pill-${tone} ${className}`}>{children}</span>;

/** One deployment in the host-resolution panel: its URL, what it serves, and its last probe. */
const HostCard = ({ tag, tone, url, probe, empty, note }) => {
    const hostState = () => {
        if (!url) return 'none';
        if (probe === null || probe === undefined) return 'unknown';
        if (probe.ok) return 'live';
        // The host answered and only the browser dropped the reply — a config problem on
        // this page's side, not an outage, so it must not read the same as a dead host.
        return probe.blocked ? 'blocked' : 'down';
    };
    const state = hostState();
    const stateText = {
        none: 'Not configured',
        unknown: 'Not checked',
        live: `Live · ${probe?.duration} ms`,
        blocked: 'Up · blocked by CORS',
        down: probe?.timedOut ? 'No response · timed out' : probe?.statusCode ? `Down · HTTP ${probe.statusCode}` : 'Unreachable'
    }[state];

    return (
        <div className={`gb-host gb-host-${state}`}>
            <div className="gb-host-top">
                <span className={`gb-hostTag gb-hostTag-${tone}`}>{tag}</span>
                <span className={`gb-host-state gb-host-state-${state}`}>
                    <span className="gb-host-dot" />
                    {stateText}
                </span>
            </div>
            <div className="gb-host-url">{url || empty}</div>
            <div className="gb-host-note">{state === 'blocked' ? "Host is reachable. Add this page's origin to that deployment's Config/Cors.php allowedOrigins." : note}</div>
        </div>
    );
};

/** Result panel: status strip + JSON viewer, shared by GET steps and POST steps. */
const ResultPanel = ({ result, maxHeight = 260 }) => {
    const [copied, setCopied] = useState(false);
    if (!result) return null;

    const ok = result.status === 'success';
    const text = rawResultText(result.data);

    const copy = () => {
        if (!navigator.clipboard) return;
        navigator.clipboard.writeText(text).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1600);
        });
    };

    return (
        <div className={`gb-res ${ok ? 'is-ok' : 'is-err'}`}>
            <div className="gb-res-bar">
                <span className="gb-res-dot" />
                <span className="gb-res-code">{ok ? `${result.statusCode} OK` : `Error ${result.statusCode || ''}`}</span>
                <span className="gb-res-meta">
                    <i className="pi pi-clock" /> {result.duration} ms
                </span>
                {result.testedAt && (
                    <span className="gb-res-meta gb-hide-sm">
                        <i className="pi pi-calendar" /> {result.testedAt}
                    </span>
                )}
                <span className="gb-res-spacer" />
                <button type="button" className="gb-copy" onClick={copy}>
                    <i className={copied ? 'pi pi-check' : 'pi pi-copy'} /> {copied ? 'Copied' : 'Copy'}
                </button>
            </div>
            <pre className="gb-res-body" style={{ maxHeight }}>
                {text}
            </pre>
        </div>
    );
};

const StepHint = ({ icon = 'pi pi-info-circle', children }) => (
    <div className="gb-hint">
        <i className={icon} />
        <span>{children}</span>
    </div>
);

const StepLoading = ({ children }) => (
    <div className="gb-hint gb-hint-loading">
        <i className="pi pi-spin pi-spinner" />
        <span>{children}</span>
    </div>
);

/** Minimal, uniform block for a GET/no-body step: request URL + run button, status/JSON. */
const StepBlock = ({ title, action, method, url, loading, loadingText, result, emptyHint, maxHeight = 260 }) => (
    <div className="gb-step">
        {title && <div className="gb-step-title">{title}</div>}
        <div className="gb-reqbar">
            <span className={`gb-reqbar-method gb-method-${method.toLowerCase()}`}>{method}</span>
            <span className="gb-reqbar-url">{url}</span>
            <span className="gb-reqbar-action">{action}</span>
        </div>
        {loading && <StepLoading>{loadingText || 'Loading…'}</StepLoading>}
        {result ? <ResultPanel result={result} maxHeight={maxHeight} /> : !loading && <StepHint>{emptyHint}</StepHint>}
    </div>
);

/** Step for POST endpoints that need an editable JSON request body. */
const PostBodyStep = ({ title, url, bodyText, onBodyChange, onRun, loading, loadingText, result, sendLabel = 'Send', maxHeight = 260 }) => (
    <div className="gb-step">
        {title && <div className="gb-step-title">{title}</div>}
        <div className="gb-reqbar">
            <span className="gb-reqbar-method gb-method-post">POST</span>
            <span className="gb-reqbar-url">{url}</span>
            <span className="gb-reqbar-action">
                <Button label={sendLabel} icon="pi pi-send" className="p-button-sm" onClick={onRun} loading={loading} />
            </span>
        </div>

        <div className="gb-editor">
            <div className="gb-editor-bar">
                <i className="pi pi-code" />
                <span>Request body</span>
                <span className="gb-res-spacer" />
                <span className="gb-editor-tag">JSON</span>
            </div>
            <textarea className="gb-json-edit" value={bodyText} onChange={(e) => onBodyChange(e.target.value)} spellCheck={false} />
        </div>

        {loading && <StepLoading>{loadingText || 'Sending…'}</StepLoading>}
        {result && <ResultPanel result={result} maxHeight={maxHeight} />}
    </div>
);

export default function GsbillingEndpoints() {
    const toast = useRef(null);

    const [clients, setClients] = useState([]);
    const [branches, setBranches] = useState([]);
    const [branchesLoading, setBranchesLoading] = useState(false);
    const [selectedClient, setSelectedClient] = useState(null);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const branchCacheRef = useRef(new Map());

    // client_creation.base_url / server_url for the selected client, plus the last probe of each
    const [clientUrls, setClientUrls] = useState({ base_url: '', server_url: '' });
    // In dev the page is served from localhost, which the deployments' CORS config does not allow —
    // default to the proxied local backend there and to the server deployment in production.
    const [hostMode, setHostMode] = useState(IS_LOCAL ? 'local' : 'server');
    const [hostProbe, setHostProbe] = useState({ server: null, client: null });
    const [probing, setProbing] = useState(false);

    const [results, setResults] = useState({});
    const [runningKeys, setRunningKeys] = useState(new Set());

    const [postBody, setPostBody] = useState({});
    const [postResults, setPostResults] = useState({});
    const [postLoading, setPostLoading] = useState({});

    const [selectedEndpoint, setSelectedEndpoint] = useState(null);
    const [search, setSearch] = useState('');
    const [collapsedGroups, setCollapsedGroups] = useState(new Set());

    const [resetVisible, setResetVisible] = useState(false);
    const [resetLoading, setResetLoading] = useState(false);

    const toggleGroup = (group) => {
        setCollapsedGroups((prev) => {
            const next = new Set(prev);
            if (next.has(group)) next.delete(group);
            else next.add(group);
            return next;
        });
    };

    useEffect(() => {
        fetchClients();
    }, []);

    // Auto-fill a callback body with pending IDs from its matching GET endpoint, unless the user already edited it.
    useEffect(() => {
        if (!selectedEndpoint?.isCallback) return;
        const r = results[selectedEndpoint.sourceKey];
        if (r?.status !== 'success') return;
        const key = selectedEndpoint.key;
        setPostBody((prev) => {
            if (prev[key] !== undefined) return prev;
            const ids = extractIds(r.data, selectedEndpoint.idField);
            return { ...prev, [key]: JSON.stringify({ data: ids }, null, 2) };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [results, selectedEndpoint]);

    const fetchClients = async () => {
        try {
            const data = await getLiveDataClients();
            // keep base_url / server_url on the option so host resolution needs no extra request
            setClients(data.map((c) => ({ label: c.client_name, value: c.client_id, base_url: c.base_url || '', server_url: c.server_url || '' })));
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load clients' });
        }
    };

    const fetchBranches = async (clientId) => {
        const cached = branchCacheRef.current.get(clientId);
        if (cached) {
            setBranches(cached);
            return;
        }
        setBranchesLoading(true);
        try {
            const data = await getLiveDataBranches(clientId);
            const options = data.map((b) => ({ label: b.branch_name, value: b.branch_id }));
            branchCacheRef.current.set(clientId, options);
            setBranches(options);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Failed to load branches' });
        } finally {
            setBranchesLoading(false);
        }
    };

    const currentSegment = () => {
        const branchObj = branches.find((b) => b.value === selectedBranch);
        return branchObj ? branchObj.label : selectedBranch || '';
    };

    const serverUrl = trimUrl(clientUrls.server_url);
    const clientUrl = trimUrl(clientUrls.base_url);

    /**
     * Which host a given endpoint is sent to: the picked deployment, except for the endpoints
     * that read/write the client's own billing tables — those are never served by the server
     * host. Returns the base URL to call plus the reason, so the UI can explain the choice.
     */
    const resolveHost = (endpoint) => {
        const localHost = { baseUrl: '', host: 'local', why: 'This backend' };
        if (!endpoint || endpoint.host === 'local' || hostMode === 'local') return localHost;

        if (endpoint.host === 'client' || hostMode === 'client') {
            if (!clientUrl) return { ...localHost, why: 'No client base_url configured — falling back to this backend' };
            return { baseUrl: clientUrl, host: 'client', why: endpoint.host === 'client' ? 'Fixed to the client deployment — its billing tables live there' : 'Sent to the client deployment' };
        }

        if (!serverUrl) return { ...localHost, why: 'No server_url configured — falling back to this backend' };
        return { baseUrl: serverUrl, host: 'server', why: 'Sent to the server deployment' };
    };

    const resolveUrl = (endpoint) => {
        const { baseUrl } = resolveHost(endpoint);
        return `${baseUrl || displayBaseUrl()}/${endpoint.path.replace('{segment}', currentSegment())}`;
    };

    /** Probe both deployments the way the backend does, so Auto mode reflects reality. */
    const checkHosts = async () => {
        const segment = currentSegment();
        if (!segment) {
            toast.current.show({ severity: 'warn', summary: 'Branch Required', detail: 'Select a client and branch first' });
            return;
        }
        setProbing(true);
        try {
            const [server, client] = await Promise.all([serverUrl ? probeGsbillingHost(serverUrl, segment) : Promise.resolve(null), clientUrl ? probeGsbillingHost(clientUrl, segment) : Promise.resolve(null)]);
            setHostProbe({ server, client });
        } finally {
            setProbing(false);
        }
    };

    const runEndpoint = async (endpoint) => {
        const needsSegment = endpoint.path.includes('{segment}');
        if (needsSegment && !selectedBranch) {
            toast.current.show({ severity: 'warn', summary: 'Branch Required', detail: 'Please select a client and branch first' });
            return;
        }
        setRunningKeys((prev) => new Set(prev).add(endpoint.key));
        const t0 = Date.now();
        try {
            const data = await ENDPOINT_HANDLERS[endpoint.key](currentSegment(), resolveHost(endpoint).baseUrl);
            setResults((prev) => ({
                ...prev,
                [endpoint.key]: { status: 'success', statusCode: 200, data, duration: Date.now() - t0, testedAt: new Date().toLocaleTimeString() }
            }));
        } catch (err) {
            setResults((prev) => ({
                ...prev,
                [endpoint.key]: { status: 'error', statusCode: err.response?.status || 0, data: err.response?.data || describeError(err, endpoint), duration: Date.now() - t0, testedAt: new Date().toLocaleTimeString() }
            }));
        } finally {
            setRunningKeys((prev) => {
                const n = new Set(prev);
                n.delete(endpoint.key);
                return n;
            });
        }
    };

    const runPostBody = async (endpoint, { requiresBranch = true, buildRequest, defaultBody }) => {
        if (requiresBranch && !selectedBranch) {
            toast.current.show({ severity: 'warn', summary: 'Branch Required', detail: 'Please select a client and branch first' });
            return;
        }
        const key = endpoint.key;
        const bodyText = postBody[key] ?? defaultBody;
        let parsed;
        try {
            parsed = bodyText.trim() ? JSON.parse(bodyText) : {};
        } catch (e) {
            setPostResults((prev) => ({ ...prev, [key]: { status: 'error', statusCode: 0, data: `Invalid JSON: ${e.message}`, duration: 0, testedAt: new Date().toLocaleTimeString() } }));
            return;
        }

        setPostLoading((prev) => ({ ...prev, [key]: true }));
        const t0 = Date.now();
        try {
            const data = await buildRequest(currentSegment(), parsed, resolveHost(endpoint).baseUrl);
            setPostResults((prev) => ({ ...prev, [key]: { status: 'success', statusCode: 200, data, duration: Date.now() - t0, testedAt: new Date().toLocaleTimeString() } }));
        } catch (err) {
            setPostResults((prev) => ({
                ...prev,
                [key]: { status: 'error', statusCode: err.response?.status || 0, data: err.response?.data || describeError(err, endpoint), duration: Date.now() - t0, testedAt: new Date().toLocaleTimeString() }
            }));
        } finally {
            setPostLoading((prev) => ({ ...prev, [key]: false }));
        }
    };

    const resetMasters = async () => {
        const segment = currentSegment();
        const t0 = Date.now();
        setResetLoading(true);
        try {
            const data = await resetLiveDataMasters(segment);
            setResults((prev) => ({
                ...prev,
                reset_masters: { status: 'success', statusCode: 200, data, duration: Date.now() - t0, testedAt: new Date().toLocaleTimeString() }
            }));
            toast.current.show({ severity: 'success', summary: 'Reset Successful', detail: `All masters reset for branch ${segment}` });
            setResetVisible(false);
        } catch (err) {
            setResults((prev) => ({
                ...prev,
                reset_masters: { status: 'error', statusCode: err.response?.status || 0, data: err.response?.data || err.message, duration: Date.now() - t0, testedAt: new Date().toLocaleTimeString() }
            }));
            toast.current.show({ severity: 'error', summary: 'Reset Failed', detail: err.response?.data?.message || 'Something went wrong' });
        } finally {
            setResetLoading(false);
        }
    };

    const selectEndpoint = (endpoint) => setSelectedEndpoint(endpoint);

    const statusInfo = (endpoint) => {
        if (runningKeys.has(endpoint.key) || postLoading[endpoint.key]) return { text: 'Running…', severity: 'info' };
        const r = postResults[endpoint.key] || results[endpoint.key];
        if (!r) return { text: 'Not Tested', severity: 'secondary' };
        return r.status === 'success' ? { text: `${r.statusCode} OK`, severity: 'success' } : { text: `Error ${r.statusCode || ''}`, severity: 'danger' };
    };

    const successCount = VISIBLE_ENDPOINTS.filter((e) => statusInfo(e).severity === 'success').length;
    const failCount = VISIBLE_ENDPOINTS.filter((e) => statusInfo(e).severity === 'danger').length;
    const notTested = VISIBLE_ENDPOINTS.length - successCount - failCount;
    const coverage = Math.round(((successCount + failCount) / VISIBLE_ENDPOINTS.length) * 100);

    const groupedEndpoints = useMemo(() => {
        const q = search.trim().toLowerCase();
        const filtered = q ? VISIBLE_ENDPOINTS.filter((e) => e.label.toLowerCase().includes(q)) : VISIBLE_ENDPOINTS;
        const groups = {};
        filtered.forEach((e) => {
            groups[e.group] = groups[e.group] || [];
            groups[e.group].push(e);
        });
        return groups;
    }, [search]);

    const renderDetailBody = () => {
        if (!selectedEndpoint) return null;

        if (selectedEndpoint.key === 'login_authenticate') {
            return (
                <PostBodyStep
                    title="Login is answered by the resolved host, but its bill ids are always read from the client deployment (base_url) — that is the only database with this branch's billing tables."
                    url={resolveUrl(selectedEndpoint)}
                    bodyText={postBody[selectedEndpoint.key] ?? LOGIN_TEMPLATE}
                    onBodyChange={(v) => setPostBody((prev) => ({ ...prev, [selectedEndpoint.key]: v }))}
                    onRun={() => runPostBody(selectedEndpoint, { requiresBranch: false, buildRequest: (_, parsed, baseUrl) => gsbillingLoginAuthenticate(parsed, baseUrl), defaultBody: LOGIN_TEMPLATE })}
                    loading={!!postLoading[selectedEndpoint.key]}
                    loadingText="Authenticating…"
                    result={postResults[selectedEndpoint.key]}
                    sendLabel="Login"
                />
            );
        }

        if (selectedEndpoint.key === 'counterdata_synching') {
            return (
                <PostBodyStep
                    url={resolveUrl(selectedEndpoint)}
                    bodyText={postBody[selectedEndpoint.key] ?? COUNTERDATA_TEMPLATE}
                    onBodyChange={(v) => setPostBody((prev) => ({ ...prev, [selectedEndpoint.key]: v }))}
                    onRun={() =>
                        runPostBody(selectedEndpoint, { requiresBranch: true, buildRequest: (segment, parsed, baseUrl) => syncGsbillingCounterData(segment, parsed, baseUrl), defaultBody: COUNTERDATA_TEMPLATE })
                    }
                    loading={!!postLoading[selectedEndpoint.key]}
                    loadingText="Syncing counter data…"
                    result={postResults[selectedEndpoint.key]}
                    sendLabel="Sync"
                    maxHeight={320}
                />
            );
        }

        if (selectedEndpoint.key === 'reset_masters') {
            return (
                <StepBlock
                    method="POST"
                    url={resolveUrl(selectedEndpoint)}
                    action={<Button label="Reset Masters" icon="pi pi-database" className="p-button-sm gb-btn-danger" onClick={() => setResetVisible(true)} disabled={!selectedBranch} />}
                    result={results[selectedEndpoint.key]}
                    emptyHint="Select a branch and click Reset Masters to wipe and re-flag all master tables for that branch. This cannot be undone."
                    maxHeight={320}
                />
            );
        }

        if (selectedEndpoint.isCallback) {
            const sourceLabel = ENDPOINTS.find((e) => e.key === selectedEndpoint.sourceKey)?.label;
            return (
                <PostBodyStep
                    title={results[selectedEndpoint.sourceKey] ? `IDs pre-filled from the last ${sourceLabel} fetch — edit if needed.` : `Run ${sourceLabel} first to pre-fill the pending IDs, or paste them below.`}
                    url={resolveUrl(selectedEndpoint)}
                    bodyText={postBody[selectedEndpoint.key] ?? CALLBACK_TEMPLATE}
                    onBodyChange={(v) => setPostBody((prev) => ({ ...prev, [selectedEndpoint.key]: v }))}
                    onRun={() =>
                        runPostBody(selectedEndpoint, {
                            requiresBranch: true,
                            buildRequest: (segment, parsed, baseUrl) => CALLBACK_HANDLERS[selectedEndpoint.key](segment, parsed, baseUrl),
                            defaultBody: CALLBACK_TEMPLATE
                        })
                    }
                    loading={!!postLoading[selectedEndpoint.key]}
                    loadingText="Acknowledging…"
                    result={postResults[selectedEndpoint.key]}
                    sendLabel="Acknowledge"
                />
            );
        }

        // Remaining endpoints take only the branch segment — select a branch, run, inspect the response.
        return (
            <StepBlock
                method={selectedEndpoint.method}
                url={resolveUrl(selectedEndpoint)}
                action={<Button label="Run" icon="pi pi-play" className="p-button-sm" onClick={() => runEndpoint(selectedEndpoint)} loading={runningKeys.has(selectedEndpoint.key)} />}
                result={results[selectedEndpoint.key]}
                emptyHint={NO_BODY_HINTS[selectedEndpoint.key] || `Select a branch and click Run to fetch pending ${selectedEndpoint.label.toLowerCase()} rows.`}
                maxHeight={320}
            />
        );
    };

    const detailStyle = selectedEndpoint ? groupStyle(selectedEndpoint.group) : DEFAULT_GROUP_STYLE;

    return (
        <div className="gsbilling-page">
            <Toast ref={toast} />
            <style>{`
                .gsbilling-page { padding: 1.25rem; background: #f4f5fa; min-height: 100%; }

                /* ── Hero ───────────────────────────────────────────── */
                .gsbilling-page .gb-hero { position: relative; overflow: hidden; border-radius: 18px; padding: 22px 26px; margin-bottom: 16px;
                    background: linear-gradient(135deg, #4c1d95 0%, #5b2fa0 45%, #7c3aed 100%);
                    box-shadow: 0 12px 30px -12px rgba(76, 29, 149, 0.55); display: flex; gap: 24px; align-items: center; flex-wrap: wrap; }
                .gsbilling-page .gb-hero::after { content: ''; position: absolute; right: -70px; top: -110px; width: 320px; height: 320px; border-radius: 50%;
                    background: radial-gradient(circle, rgba(255,255,255,0.16) 0%, rgba(255,255,255,0) 68%); pointer-events: none; }
                .gsbilling-page .gb-hero-main { display: flex; align-items: center; gap: 16px; flex: 1; min-width: 280px; position: relative; z-index: 1; }
                .gsbilling-page .gb-hero-icon { width: 52px; height: 52px; flex-shrink: 0; border-radius: 14px; display: flex; align-items: center; justify-content: center;
                    background: rgba(255,255,255,0.16); border: 1px solid rgba(255,255,255,0.25); backdrop-filter: blur(6px); }
                .gsbilling-page .gb-hero-icon i { font-size: 1.5rem; color: #fff; }
                .gsbilling-page .gb-hero-title { margin: 0; font-size: 1.4rem; font-weight: 800; color: #fff; letter-spacing: -0.02em; line-height: 1.2; }
                .gsbilling-page .gb-hero-sub { margin: 5px 0 0; font-size: 0.82rem; color: rgba(255,255,255,0.78); max-width: 640px; line-height: 1.45; }

                .gsbilling-page .gb-stats { display: flex; gap: 10px; flex-wrap: wrap; position: relative; z-index: 1; }
                .gsbilling-page .gb-stat { min-width: 96px; padding: 10px 14px; border-radius: 12px; background: rgba(255,255,255,0.12);
                    border: 1px solid rgba(255,255,255,0.18); backdrop-filter: blur(8px); transition: transform .18s ease, background-color .18s ease; }
                .gsbilling-page .gb-stat:hover { transform: translateY(-2px); background: rgba(255,255,255,0.18); }
                .gsbilling-page .gb-stat-value { font-size: 1.4rem; font-weight: 800; color: #fff; line-height: 1.1; display: flex; align-items: center; gap: 7px; }
                .gsbilling-page .gb-stat-value i { font-size: 0.8rem; }
                .gsbilling-page .gb-stat-label { font-size: 0.63rem; font-weight: 700; letter-spacing: 0.09em; text-transform: uppercase; color: rgba(255,255,255,0.7); margin-top: 3px; }
                .gsbilling-page .gb-stat.ok .gb-stat-value i { color: #6ee7b7; }
                .gsbilling-page .gb-stat.err .gb-stat-value i { color: #fda4af; }
                .gsbilling-page .gb-stat.idle .gb-stat-value i { color: #cbd5e1; }
                .gsbilling-page .gb-stat.all .gb-stat-value i { color: #c4b5fd; }
                .gsbilling-page .gb-progress { height: 4px; border-radius: 4px; background: rgba(255,255,255,0.2); margin-top: 8px; overflow: hidden; }
                .gsbilling-page .gb-progress span { display: block; height: 100%; border-radius: 4px; background: linear-gradient(90deg, #a78bfa, #6ee7b7); transition: width .4s ease; }

                /* ── Toolbar ────────────────────────────────────────── */
                .gsbilling-page .gb-toolbar { background: #fff; border: 1px solid #e8eaf2; border-radius: 16px; padding: 14px 18px; margin-bottom: 16px;
                    box-shadow: 0 2px 10px -4px rgba(15, 23, 42, 0.10); display: flex; gap: 16px; align-items: flex-end; flex-wrap: wrap; }
                .gsbilling-page .gb-field { display: flex; flex-direction: column; flex: 1; min-width: 210px; }
                .gsbilling-page .gb-field-label { font-size: 0.68rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: #7c3aed; margin-bottom: 6px; display: flex; align-items: center; gap: 6px; }
                .gsbilling-page .gb-field-label .gb-muted { color: #a1a1b5; font-weight: 600; letter-spacing: 0.04em; text-transform: none; }
                .gsbilling-page .gb-field .p-dropdown { border-radius: 10px; border-color: #e2e5ef; }
                .gsbilling-page .gb-field .p-dropdown:not(.p-disabled):hover { border-color: #c4b5fd; }
                .gsbilling-page .gb-field .p-dropdown:not(.p-disabled).p-focus { border-color: #7c3aed; box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.14); }
                .gsbilling-page .gb-segment { display: inline-flex; align-items: center; gap: 7px; padding: 8px 13px; border-radius: 10px; background: #f5f3ff;
                    border: 1px dashed #c4b5fd; color: #5b21b6; font-size: 0.78rem; font-weight: 600; font-family: ui-monospace, Menlo, Consolas, monospace; white-space: nowrap; }
                .gsbilling-page .gb-segment i { font-size: 0.75rem; }
                .gsbilling-page .gb-clear.p-button { border-radius: 10px; background: #fff; border: 1px solid #e2e5ef; color: #64748b; font-weight: 600; }
                .gsbilling-page .gb-clear.p-button:enabled:hover { background: #f8fafc; border-color: #cbd5e1; color: #475569; }

                /* ── Host resolution ────────────────────────────────── */
                .gsbilling-page .gb-hosts { background: #fff; border: 1px solid #e8eaf2; border-radius: 16px; padding: 14px 18px 12px; margin-bottom: 16px;
                    box-shadow: 0 2px 10px -4px rgba(15, 23, 42, 0.10); }
                .gsbilling-page .gb-hosts-head { display: flex; align-items: center; gap: 8px; font-size: 0.72rem; font-weight: 800; letter-spacing: 0.08em;
                    text-transform: uppercase; color: #7c3aed; margin-bottom: 12px; }
                .gsbilling-page .gb-check.p-button { background: linear-gradient(135deg, #6d28d9, #7c3aed) !important; border: none !important; font-weight: 600; border-radius: 9px; }
                .gsbilling-page .gb-hosts-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
                .gsbilling-page .gb-host { border: 1px solid #e8eaf2; border-radius: 12px; padding: 11px 13px; background: #fbfbfe; }
                .gsbilling-page .gb-host-live { border-color: #a7f3d0; background: #f0fdf9; }
                .gsbilling-page .gb-host-down { border-color: #fecaca; background: #fef6f6; }
                .gsbilling-page .gb-host-blocked { border-color: #fde68a; background: #fffbeb; }
                .gsbilling-page .gb-host-none { opacity: 0.72; }
                .gsbilling-page .gb-host-top { display: flex; align-items: center; gap: 8px; margin-bottom: 7px; }
                .gsbilling-page .gb-host-state { display: inline-flex; align-items: center; gap: 6px; margin-left: auto; font-size: 0.68rem; font-weight: 700; color: #64748b; }
                .gsbilling-page .gb-host-dot { width: 7px; height: 7px; border-radius: 50%; background: #cbd5e1; }
                .gsbilling-page .gb-host-state-live { color: #047857; }
                .gsbilling-page .gb-host-state-live .gb-host-dot { background: #34d399; box-shadow: 0 0 0 3px rgba(52, 211, 153, 0.22); }
                .gsbilling-page .gb-host-state-down { color: #b91c1c; }
                .gsbilling-page .gb-host-state-down .gb-host-dot { background: #fb7185; box-shadow: 0 0 0 3px rgba(251, 113, 133, 0.22); }
                .gsbilling-page .gb-host-state-blocked { color: #b45309; }
                .gsbilling-page .gb-host-state-blocked .gb-host-dot { background: #fbbf24; box-shadow: 0 0 0 3px rgba(251, 191, 36, 0.24); }
                .gsbilling-page .gb-host-url { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 0.76rem; color: #334155; word-break: break-all; line-height: 1.45; }
                .gsbilling-page .gb-host-note { margin-top: 5px; font-size: 0.7rem; color: #94a3b8; line-height: 1.4; }
                .gsbilling-page .gb-hosts-foot { display: flex; align-items: flex-start; gap: 8px; margin-top: 11px; padding-top: 10px; border-top: 1px dashed #eef0f6;
                    font-size: 0.73rem; color: #7c8598; line-height: 1.5; word-break: break-all; }
                .gsbilling-page .gb-hosts-foot i { color: #a78bfa; margin-top: 2px; }

                /* Host tags */
                .gsbilling-page .gb-hostTag { display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 6px; font-size: 0.62rem;
                    font-weight: 800; letter-spacing: 0.07em; border: 1px solid transparent; white-space: nowrap; }
                .gsbilling-page .gb-hostTag-sm { font-size: 0.56rem; padding: 1px 6px; }
                .gsbilling-page .gb-hostTag-server { background: #eef2ff; color: #4338ca; border-color: #c7d2fe; }
                .gsbilling-page .gb-hostTag-client { background: #fdf4ff; color: #a21caf; border-color: #f5d0fe; }
                .gsbilling-page .gb-detail-why { margin-top: 7px; display: flex; align-items: center; gap: 7px; font-size: 0.73rem; color: #7c8598; }
                .gsbilling-page .gb-detail-why i { color: #a78bfa; font-size: 0.72rem; }

                @media (max-width: 900px) { .gsbilling-page .gb-hosts-grid { grid-template-columns: 1fr; } }

                /* ── Split view ─────────────────────────────────────── */
                .gsbilling-page .gb-split { display: flex; gap: 16px; height: 70vh; min-height: 500px; }
                .gsbilling-page .gb-sidebar, .gsbilling-page .gb-detail { background: #fff; border: 1px solid #e8eaf2; border-radius: 16px;
                    box-shadow: 0 2px 10px -4px rgba(15, 23, 42, 0.10); overflow-y: auto; }
                .gsbilling-page .gb-sidebar { width: 340px; flex-shrink: 0; }
                .gsbilling-page .gb-detail { flex: 1; display: flex; flex-direction: column; }
                .gsbilling-page .gb-sidebar::-webkit-scrollbar, .gsbilling-page .gb-detail::-webkit-scrollbar, .gsbilling-page .gb-res-body::-webkit-scrollbar { width: 8px; height: 8px; }
                .gsbilling-page .gb-sidebar::-webkit-scrollbar-thumb, .gsbilling-page .gb-detail::-webkit-scrollbar-thumb { background: #dfe3ec; border-radius: 8px; }
                .gsbilling-page .gb-sidebar::-webkit-scrollbar-thumb:hover, .gsbilling-page .gb-detail::-webkit-scrollbar-thumb:hover { background: #c7cddb; }
                .gsbilling-page .gb-res-body::-webkit-scrollbar-thumb { background: #334155; border-radius: 8px; }

                /* Search */
                .gsbilling-page .gb-search { position: sticky; top: 0; z-index: 3; background: #fff; padding: 12px; border-bottom: 1px solid #eef0f6; }
                .gsbilling-page .gb-search .p-input-icon-left { width: 100%; }
                .gsbilling-page .gb-search .p-inputtext { width: 100%; border-radius: 10px; background: #f7f8fc; border-color: #e8eaf2; font-size: 0.85rem; padding-left: 2.4rem; }
                .gsbilling-page .gb-search .p-inputtext:enabled:focus { background: #fff; border-color: #7c3aed; box-shadow: 0 0 0 3px rgba(124, 58, 237, 0.12); }
                .gsbilling-page .gb-search i { color: #a5abbd; }

                /* Group headers */
                .gsbilling-page .gb-group-header { padding: 9px 14px; font-weight: 800; font-size: 0.68rem; text-transform: uppercase; letter-spacing: 0.09em;
                    position: sticky; top: 61px; z-index: 2; display: flex; align-items: center; gap: 8px; user-select: none; cursor: pointer; transition: filter .15s ease; }
                .gsbilling-page .gb-group-header:hover { filter: brightness(0.97); }
                .gsbilling-page .gb-group-count { font-weight: 700; font-size: 0.68rem; padding: 1px 8px; border-radius: 999px; background: rgba(255,255,255,0.72); }

                /* List items */
                .gsbilling-page .gb-item { cursor: pointer; padding: 11px 14px 11px 15px; border-left: 3px solid transparent; border-bottom: 1px solid #f1f3f8;
                    transition: background-color .15s ease, border-color .15s ease, padding-left .15s ease; }
                .gsbilling-page .gb-item:hover { background: #f8f9fd; padding-left: 18px; }
                .gsbilling-page .gb-item.selected { background: linear-gradient(90deg, #f5f3ff 0%, #fbfaff 100%); }
                .gsbilling-page .gb-item.selected .gb-item-title { color: #5b21b6; }
                .gsbilling-page .gb-item-row { display: flex; align-items: center; gap: 8px; }
                .gsbilling-page .gb-item-title { font-weight: 600; font-size: 0.85rem; color: #1e293b; flex: 1; line-height: 1.3; }
                .gsbilling-page .gb-item-meta { font-size: 0.7rem; color: #a5abbd; }
                .gsbilling-page .gb-item-foot { display: flex; align-items: center; gap: 8px; margin-top: 6px; }
                .gsbilling-page .gb-empty-search { padding: 26px 16px; text-align: center; color: #a5abbd; font-size: 0.83rem; }

                /* Pills */
                .gsbilling-page .gb-pill { display: inline-flex; align-items: center; gap: 4px; padding: 2px 8px; border-radius: 999px;
                    font-size: 0.63rem; font-weight: 700; letter-spacing: 0.03em; line-height: 1.6; border: 1px solid transparent; white-space: nowrap; }
                .gsbilling-page .gb-pill-get { background: #ecfdf5; color: #047857; border-color: #a7f3d0; }
                .gsbilling-page .gb-pill-post { background: #fff7ed; color: #b45309; border-color: #fed7aa; }
                .gsbilling-page .gb-pill-success { background: #ecfdf5; color: #047857; border-color: #a7f3d0; }
                .gsbilling-page .gb-pill-danger { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
                .gsbilling-page .gb-pill-secondary { background: #f4f5f9; color: #64748b; border-color: #e2e5ef; }
                .gsbilling-page .gb-pill-info { background: #eff6ff; color: #1d4ed8; border-color: #bfdbfe; }

                /* Detail header */
                .gsbilling-page .gb-detail-header { padding: 18px 24px; border-bottom: 1px solid #eef0f6; position: sticky; top: 0; z-index: 2; backdrop-filter: blur(6px); }
                .gsbilling-page .gb-crumb { display: flex; align-items: center; gap: 8px; font-size: 0.7rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; margin-bottom: 8px; }
                .gsbilling-page .gb-detail-title { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; }
                .gsbilling-page .gb-detail-title h3 { margin: 0; font-size: 1.28rem; font-weight: 800; color: #1e293b; letter-spacing: -0.02em; }
                .gsbilling-page .gb-detail-path { margin-top: 8px; font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 0.74rem; color: #94a3b8; word-break: break-all; }
                .gsbilling-page .gb-detail-body { padding: 18px 24px 24px; }

                /* Steps */
                .gsbilling-page .gb-step { padding: 2px 0; }
                .gsbilling-page .gb-step-title { font-weight: 600; font-size: 0.82rem; color: #475569; margin-bottom: 10px; padding: 9px 12px;
                    background: #f8f9fd; border: 1px solid #eef0f6; border-left: 3px solid #a78bfa; border-radius: 8px; }
                .gsbilling-page .gb-hint { display: flex; align-items: flex-start; gap: 9px; margin-top: 12px; padding: 12px 14px; border-radius: 10px;
                    background: #f8f9fd; border: 1px dashed #e2e5ef; color: #7c8598; font-size: 0.8rem; line-height: 1.5; }
                .gsbilling-page .gb-hint i { color: #a78bfa; margin-top: 2px; }
                .gsbilling-page .gb-hint-loading { background: #f5f3ff; border-color: #ddd6fe; color: #6d28d9; }

                /* Request bar */
                .gsbilling-page .gb-reqbar { display: flex; align-items: stretch; border: 1px solid #e2e5ef; border-radius: 12px; overflow: hidden;
                    background: #fbfbfe; box-shadow: 0 1px 3px rgba(15, 23, 42, 0.05); }
                .gsbilling-page .gb-reqbar-method { display: flex; align-items: center; padding: 0 15px; font-weight: 800; font-size: 0.74rem;
                    font-family: ui-monospace, Menlo, Consolas, monospace; letter-spacing: 0.06em; border-right: 1px solid #e2e5ef; white-space: nowrap; }
                .gsbilling-page .gb-method-get { color: #047857; background: #ecfdf5; }
                .gsbilling-page .gb-method-post { color: #b45309; background: #fff7ed; }
                .gsbilling-page .gb-reqbar-url { flex: 1; display: flex; align-items: center; padding: 11px 14px; font-family: ui-monospace, Menlo, Consolas, monospace;
                    font-size: 0.78rem; color: #475569; word-break: break-all; min-width: 0; }
                .gsbilling-page .gb-reqbar-action { display: flex; align-items: stretch; }
                .gsbilling-page .gb-reqbar-action .p-button { height: 100%; border-radius: 0 !important; padding-left: 18px; padding-right: 18px; font-weight: 600;
                    background: linear-gradient(135deg, #6d28d9, #7c3aed) !important; border: none !important; box-shadow: none !important; }
                .gsbilling-page .gb-reqbar-action .p-button:enabled:hover { background: linear-gradient(135deg, #5b21b6, #6d28d9) !important; }
                .gsbilling-page .gb-reqbar-action .p-button:disabled { background: #cbd5e1 !important; opacity: 1; }
                .gsbilling-page .gb-reqbar-action .gb-btn-danger.p-button { background: linear-gradient(135deg, #e11d48, #f43f5e) !important; }
                .gsbilling-page .gb-reqbar-action .gb-btn-danger.p-button:enabled:hover { background: linear-gradient(135deg, #be123c, #e11d48) !important; }

                /* JSON editor */
                .gsbilling-page .gb-editor { margin-top: 12px; border-radius: 12px; overflow: hidden; border: 1px solid #1e293b; }
                .gsbilling-page .gb-editor-bar { display: flex; align-items: center; gap: 8px; padding: 8px 13px; background: #111827;
                    color: #94a3b8; font-size: 0.72rem; font-weight: 600; letter-spacing: 0.04em; }
                .gsbilling-page .gb-editor-tag { padding: 1px 8px; border-radius: 999px; background: rgba(148, 163, 184, 0.16); color: #cbd5e1; font-size: 0.62rem; font-weight: 700; }
                .gsbilling-page .gb-json-edit { width: 100%; min-height: 150px; resize: vertical; box-sizing: border-box; display: block; border: none; outline: none;
                    background: #0f172a; color: #e2e8f0; font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 0.78rem; line-height: 1.6; padding: 13px 15px; }
                .gsbilling-page .gb-json-edit::selection { background: rgba(124, 58, 237, 0.45); }

                /* Response viewer */
                .gsbilling-page .gb-res { margin-top: 12px; border-radius: 12px; overflow: hidden; border: 1px solid #1e293b; }
                .gsbilling-page .gb-res-bar { display: flex; align-items: center; gap: 12px; padding: 8px 13px; background: #111827; }
                .gsbilling-page .gb-res-dot { width: 8px; height: 8px; border-radius: 50%; flex-shrink: 0; }
                .gsbilling-page .gb-res.is-ok .gb-res-dot { background: #34d399; box-shadow: 0 0 0 3px rgba(52, 211, 153, 0.2); }
                .gsbilling-page .gb-res.is-err .gb-res-dot { background: #fb7185; box-shadow: 0 0 0 3px rgba(251, 113, 133, 0.2); }
                .gsbilling-page .gb-res-code { font-size: 0.74rem; font-weight: 800; letter-spacing: 0.04em; font-family: ui-monospace, Menlo, Consolas, monospace; }
                .gsbilling-page .gb-res.is-ok .gb-res-code { color: #6ee7b7; }
                .gsbilling-page .gb-res.is-err .gb-res-code { color: #fda4af; }
                .gsbilling-page .gb-res-meta { display: inline-flex; align-items: center; gap: 5px; font-size: 0.7rem; color: #94a3b8; }
                .gsbilling-page .gb-res-meta i { font-size: 0.68rem; }
                .gsbilling-page .gb-res-spacer { flex: 1; }
                .gsbilling-page .gb-copy { display: inline-flex; align-items: center; gap: 5px; padding: 3px 10px; border-radius: 7px; cursor: pointer;
                    background: rgba(148, 163, 184, 0.14); border: 1px solid rgba(148, 163, 184, 0.2); color: #cbd5e1; font-size: 0.68rem; font-weight: 600; transition: background-color .15s ease; }
                .gsbilling-page .gb-copy:hover { background: rgba(148, 163, 184, 0.26); color: #fff; }
                .gsbilling-page .gb-res-body { margin: 0; padding: 13px 15px; background: #0f172a; color: #e2e8f0; overflow: auto;
                    font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 0.76rem; line-height: 1.6; white-space: pre-wrap; word-break: break-word; }

                /* Empty detail state */
                .gsbilling-page .gb-empty { display: flex; flex-direction: column; align-items: center; justify-content: center; flex: 1; text-align: center; padding: 40px; }
                .gsbilling-page .gb-empty-icon { width: 88px; height: 88px; border-radius: 50%; display: flex; align-items: center; justify-content: center;
                    background: linear-gradient(135deg, #f5f3ff, #ede9fe); margin-bottom: 18px; }
                .gsbilling-page .gb-empty-icon i { font-size: 2.2rem; color: #a78bfa; }
                .gsbilling-page .gb-empty h4 { margin: 0 0 6px; font-size: 1.02rem; font-weight: 700; color: #475569; }
                .gsbilling-page .gb-empty p { margin: 0; font-size: 0.84rem; color: #94a3b8; max-width: 380px; line-height: 1.55; }

                @media (max-width: 1100px) {
                    .gsbilling-page .gb-split { flex-direction: column; height: auto; }
                    .gsbilling-page .gb-sidebar { width: 100%; max-height: 360px; }
                    .gsbilling-page .gb-detail { min-height: 440px; }
                }
                @media (max-width: 640px) {
                    .gsbilling-page { padding: 0.75rem; }
                    .gsbilling-page .gb-hero { padding: 18px; }
                    .gsbilling-page .gb-stat { flex: 1; min-width: 78px; }
                    .gsbilling-page .gb-reqbar { flex-direction: column; align-items: stretch; }
                    .gsbilling-page .gb-reqbar-method { border-right: none; border-bottom: 1px solid #e2e5ef; padding: 8px 14px; }
                    .gsbilling-page .gb-reqbar-action .p-button { width: 100%; }
                    .gsbilling-page .gb-hide-sm { display: none; }
                    .gsbilling-page .gb-detail-header, .gsbilling-page .gb-detail-body { padding-left: 16px; padding-right: 16px; }
                }
            `}</style>

            {/* ── Hero ───────────────────────────────────────────── */}
            <div className="gb-hero">
                <div className="gb-hero-main">
                    <div className="gb-hero-icon">
                        <i className="pi pi-server" />
                    </div>
                    <div>
                        <h2 className="gb-hero-title">GS Billing — Endpoint Tester</h2>
                        <p className="gb-hero-sub">
                            Select a client and branch, then pick an endpoint on the left — request URL, body and live response appear on the right. Master-data endpoints go to the host picked above; bill sync,
                            bill details and SMS always stay on the client's base_url.
                        </p>
                    </div>
                </div>

                <div className="gb-stats">
                    <div className="gb-stat ok">
                        <div className="gb-stat-value">
                            <i className="pi pi-check-circle" />
                            {successCount}
                        </div>
                        <div className="gb-stat-label">Success</div>
                    </div>
                    <div className="gb-stat err">
                        <div className="gb-stat-value">
                            <i className="pi pi-times-circle" />
                            {failCount}
                        </div>
                        <div className="gb-stat-label">Failed</div>
                    </div>
                    <div className="gb-stat idle">
                        <div className="gb-stat-value">
                            <i className="pi pi-minus-circle" />
                            {notTested}
                        </div>
                        <div className="gb-stat-label">Not Tested</div>
                    </div>
                    <div className="gb-stat all">
                        <div className="gb-stat-value">
                            <i className="pi pi-bolt" />
                            {VISIBLE_ENDPOINTS.length}
                        </div>
                        <div className="gb-stat-label">Endpoints</div>
                        <div className="gb-progress">
                            <span style={{ width: `${coverage}%` }} />
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Toolbar ────────────────────────────────────────── */}
            <div className="gb-toolbar">
                <div className="gb-field">
                    <label className="gb-field-label">
                        <i className="pi pi-building" /> Client
                    </label>
                    <Dropdown
                        value={selectedClient}
                        options={clients}
                        onChange={(e) => {
                            const row = clients.find((c) => c.value === e.value);
                            setSelectedClient(e.value);
                            setSelectedBranch(null);
                            setBranches([]);
                            setClientUrls({ base_url: row?.base_url || '', server_url: row?.server_url || '' });
                            setHostProbe({ server: null, client: null });
                            fetchBranches(e.value);
                        }}
                        placeholder="Select Client"
                        filter
                        className="w-full"
                    />
                </div>

                <div className="gb-field">
                    <label className="gb-field-label">
                        <i className="pi pi-sitemap" /> Branch <span className="gb-muted">(segment)</span>
                        {branchesLoading && <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.75rem' }} />}
                    </label>
                    <Dropdown
                        value={selectedBranch}
                        options={branches}
                        onChange={(e) => setSelectedBranch(e.value)}
                        placeholder={branchesLoading ? 'Loading branches…' : 'Select Branch'}
                        filter
                        disabled={!selectedClient || branchesLoading}
                        className="w-full"
                    />
                </div>

                <div className="gb-field" style={{ maxWidth: 230 }}>
                    <label className="gb-field-label">
                        <i className="pi pi-server" /> Host
                    </label>
                    <Dropdown value={hostMode} options={HOST_MODES} onChange={(e) => setHostMode(e.value)} className="w-full" />
                </div>

                <div className="flex gap-2 align-items-center" style={{ paddingBottom: 1 }}>
                    {selectedBranch && (
                        <span className="gb-segment gb-hide-sm">
                            <i className="pi pi-link" />
                            {currentSegment()}
                        </span>
                    )}
                    <Button
                        label="Clear Results"
                        icon="pi pi-refresh"
                        className="gb-clear"
                        onClick={() => {
                            setResults({});
                            setPostResults({});
                            setPostBody({});
                            setSelectedClient(null);
                            setSelectedBranch(null);
                            setBranches([]);
                            setClientUrls({ base_url: '', server_url: '' });
                            setHostProbe({ server: null, client: null });
                        }}
                    />
                </div>
            </div>

            {/* ── Host resolution ────────────────────────────────── */}
            {selectedClient && (
                <div className="gb-hosts">
                    <div className="gb-hosts-head">
                        <i className="pi pi-sitemap" />
                        <span>Host resolution</span>
                        <span className="gb-res-spacer" />
                        <Button label={probing ? 'Checking…' : 'Check Hosts'} icon="pi pi-bolt" className="p-button-sm gb-check" onClick={checkHosts} loading={probing} disabled={!selectedBranch} />
                    </div>

                    <div className="gb-hosts-grid">
                        <HostCard
                            tag="SERVER"
                            tone="server"
                            url={serverUrl}
                            probe={hostProbe.server}
                            empty="No server_url set for this client — master data stays on the client deployment."
                            note="client_creation.server_url — master data & callbacks when Server is picked"
                        />
                        <HostCard
                            tag="CLIENT"
                            tone="client"
                            url={clientUrl}
                            probe={hostProbe.client}
                            empty="No base_url set for this client."
                            note="client_creation.base_url — always serves bill sync, bill details and SMS"
                        />
                    </div>

                    <div className="gb-hosts-foot">
                        <i className="pi pi-info-circle" />
                        <span>
                            {hostMode === 'local'
                                ? 'Host override: every request goes to the backend serving this page.'
                                : `Master data → ${resolveHost({ host: 'server' }).baseUrl || displayBaseUrl()} · Bill sync / SMS → ${resolveHost({ host: 'client' }).baseUrl || displayBaseUrl()}`}
                        </span>
                    </div>
                </div>
            )}

            {/* ── Split view ─────────────────────────────────────── */}
            <div className="gb-split">
                {/* Left: endpoint list */}
                <div className="gb-sidebar">
                    <div className="gb-search">
                        <span className="p-input-icon-left">
                            <i className="pi pi-search" />
                            <InputText value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search endpoints…" />
                        </span>
                    </div>

                    {Object.keys(groupedEndpoints).length === 0 && (
                        <div className="gb-empty-search">
                            <i className="pi pi-inbox" style={{ fontSize: '1.6rem', display: 'block', marginBottom: 8, color: '#cbd5e1' }} />
                            No endpoints match your search.
                        </div>
                    )}

                    {Object.entries(groupedEndpoints).map(([group, eps]) => {
                        const gs = groupStyle(group);
                        const isCollapsed = collapsedGroups.has(group);
                        return (
                            <div key={group}>
                                <div className="gb-group-header" style={{ background: gs.bg, color: gs.color, borderLeft: `4px solid ${gs.accent}` }} onClick={() => toggleGroup(group)}>
                                    <i className={gs.icon} />
                                    <span style={{ flex: 1 }}>{group}</span>
                                    <span className="gb-group-count">{eps.length}</span>
                                    <i className={`pi ${isCollapsed ? 'pi-chevron-right' : 'pi-chevron-down'}`} style={{ fontSize: '0.65rem' }} />
                                </div>
                                {!isCollapsed &&
                                    eps.map((ep) => {
                                        const info = statusInfo(ep);
                                        const isSelected = selectedEndpoint?.key === ep.key;
                                        const r = postResults[ep.key] || results[ep.key];
                                        return (
                                            <div key={ep.key} className={`gb-item${isSelected ? ' selected' : ''}`} style={{ borderLeftColor: isSelected ? gs.accent : 'transparent' }} onClick={() => selectEndpoint(ep)}>
                                                <div className="gb-item-row">
                                                    <Pill tone={ep.method.toLowerCase()}>{ep.method}</Pill>
                                                    <span className="gb-item-title">{ep.label}</span>
                                                    {ep.host !== 'local' && <span className={`gb-hostTag gb-hostTag-sm gb-hostTag-${ep.host}`}>{HOST_LABELS[ep.host]}</span>}
                                                    {(runningKeys.has(ep.key) || postLoading[ep.key]) && <i className="pi pi-spin pi-spinner" style={{ color: gs.accent, fontSize: '0.8rem' }} />}
                                                </div>
                                                <div className="gb-item-foot">
                                                    <Pill tone={info.severity}>{info.text}</Pill>
                                                    <span className="gb-item-meta">{r ? `${r.duration} ms · ${r.testedAt}` : 'Not tested yet'}</span>
                                                </div>
                                            </div>
                                        );
                                    })}
                            </div>
                        );
                    })}
                </div>

                {/* Right: detail pane */}
                <div className="gb-detail">
                    {selectedEndpoint ? (
                        <>
                            <div className="gb-detail-header" style={{ background: `linear-gradient(180deg, ${detailStyle.soft} 0%, rgba(255,255,255,0.9) 100%)` }}>
                                <div className="gb-crumb" style={{ color: detailStyle.color }}>
                                    <i className={detailStyle.icon} style={{ fontSize: '0.75rem' }} />
                                    <span>{selectedEndpoint.group}</span>
                                </div>
                                <div className="gb-detail-title">
                                    <Pill tone={selectedEndpoint.method.toLowerCase()}>{selectedEndpoint.method}</Pill>
                                    <h3>{selectedEndpoint.label}</h3>
                                    {selectedEndpoint.host !== 'local' && <span className={`gb-hostTag gb-hostTag-${selectedEndpoint.host}`}>{HOST_LABELS[selectedEndpoint.host]}</span>}
                                </div>
                                <div className="gb-detail-path">/{selectedEndpoint.path}</div>
                                <div className="gb-detail-why">
                                    <i className="pi pi-server" /> {resolveHost(selectedEndpoint).why}
                                </div>
                            </div>
                            <div className="gb-detail-body">{renderDetailBody()}</div>
                        </>
                    ) : (
                        <div className="gb-empty">
                            <div className="gb-empty-icon">
                                <i className="pi pi-send" />
                            </div>
                            <h4>No endpoint selected</h4>
                            <p>Pick an endpoint from the list to view its request URL, run it, and inspect the response.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Reset Masters confirmation */}
            <Dialog
                header="Reset All Masters"
                visible={resetVisible}
                style={{ width: '440px' }}
                onHide={() => setResetVisible(false)}
                footer={
                    <div className="flex justify-content-end gap-2">
                        <Button label="Cancel" icon="pi pi-times" className="p-button-text" onClick={() => setResetVisible(false)} disabled={resetLoading} />
                        <Button label="Reset Masters" icon="pi pi-database" className="p-button-danger" onClick={resetMasters} loading={resetLoading} />
                    </div>
                }
            >
                <div className="flex align-items-center gap-3">
                    <i className="pi pi-exclamation-triangle text-orange-500" style={{ fontSize: '2.5rem' }} />
                    <span>
                        Are you sure you want to reset all masters for branch <strong>{currentSegment()}</strong>?
                        <br />
                        <small style={{ color: '#888' }}>This cannot be undone.</small>
                    </span>
                </div>
            </Dialog>
        </div>
    );
}
